/**
 * Optimizely feature flags.
 *
 * Adding a member here is NOT enough to make a flag work. The server only asks
 * Optimizely about the keys listed in `OPTIMIZELY_FEATURE_FLAGS` in
 * `constants/features/*`, so an unregistered flag never reaches the client and
 * `useFeatureFlag` reads `false` in every environment, however it is configured
 * in Optimizely. Register the key there as well.
 */
export enum OptimizelyFeatureFlags {
  showDreamBlogLinks = 'show_dream_blog_links',
  luxPlusToursToggle = 'luxplus_tours_toggle',
  luxPlusDiscountedInsuranceBenefit = 'lux_plus_discounted_insurance_benefit',
  luxPlusDiscountedBookingProtectionBenefit = 'lux_plus_discounted_booking_protection_benefit',
  luxPlusLppPricingEnabled = 'luxplus_lpp_pricing_enabled',
  luxPlusCruiseInclusionsEnabled = 'lux_plus_cruise_inclusions',
  luxPlusDiscountedCruise = 'lux_plus_discounted_cruise',
  luxPlusDiscountedExperiencesBenefit = 'lux_plus_discounted_experiences_benefit',
  allowPhoneNumberLogin = 'allow_phone_number_login',
  promoAppBanner = 'app_banner_targeted_delivery',
  CSAgentsAutoQuote = 'cro_cs_agents_auto_quote',
  toursRequestAgentCallbackEnabled = 'csx_tours_request_agent_callback',
  hotelsAndVillasRequestAgentCallbackEnabled = 'csx_hotels_and_villas_request_agent_callback',
  upsellTilesFeatureToggle = 'cro_upsell_on_homepage_and_my_escapes',
  croTravelPreferencesModalEnabled = 'cro_travel_preferences_modal',
  opexTermsAndConditionsModal = 'terms_and_conditions_modal',
  croWebPushNotificationPrompt = 'cro_web_push_notification_prompt',
  opexFraudOrdersOtp = 'opex_fraud_orders_otp',
  payIdTargetedRelease = 'pay_id_targeted_release',
  carHireBundledDiscount = 'car_hire_bundled_discount',
  lereDestinationModelEnabled = 'lere_personalised_destinations',
  lereOfferPageRecommendationsEnabled = 'lere_offer_page',
  lereHeroCarouselEnabled = 'lere_hero_carousel',
  lereSearchRerankingEnabled = 'lere_search_reranking',
  opexHotelAddExtraGuestsEnabled = 'hotel_add_extra_guests',
  opexLPCAddExtraGuestsEnabled = 'lpc_add_extra_guests',
  flightsEnableSabreContent = 'flights_enable_sabre_content',
  flightsLuxPlusPricing = 'flights_lux_plus_pricing',
  flightsIncompleteCharterBundles = 'flights_incomplete_charter_bundles',
  flightsBundledLuxPlusPricing = 'flights_bundled_lux_plus_pricing',
  flightsCombinedTileV2 = 'flights_combined_tile_v2',
  flightsSingleLegRedesign = 'flights_single_leg_redesign',
  flightsSearchBarRedesign = 'flights_search_bar_redesign',
  /** Rollout switch for the Popular flights section on the standalone flights landing page. */
  flightsPopularFlights = 'flights_popular_flights',
  loyaltyLuxPlusRenewalReward = 'loyalty_lux_plus_renewal_reward',
  croInsuranceXCoverEnabled = 'cro_insurance_xcover_enabled',
  croPayAtProperty = 'cro_pay_at_property',
  croInclusionValidity = 'cro_inclusion_validity',
  croPackageUpgradeLibEnabled = 'cro_package_upgrade_lib_enabled',
  enableLabFeatureFlag = 'enable_lab_feature_flag',
  standaloneFlightLoungePassExperiencesEnabled = 'standalone_flight_lounge_pass_experiences_enabled',
  merchandiseLaunch = 'merchandise_launch',
  loyaltyPurchasePointsEnabled = 'loyalty_points_purchase',
  opexBookingAuthority = 'opex_booking_authority',
  opexLESAConditionalBubble = 'opex_lesa_conditional_bubble',
  opexLesaPredictedWelcomeChips = 'opex_lesa_predicted_welcome_chips',
  opexBookingAuthorityManageAccess = 'opex_booking_authority_manage_access',
  luxRow = 'lux_row',
  maldivesCharterRewardSeats = 'maldives_charter_reward_seats',
  croWrappedModalTravelled = 'cro_wrapped_modal',
  croWrappedModalAll = 'cro_wrapped_modal_all',
  croDisplaySimilarAccounts = 'cro_display_similar_accounts',
  randomizeExperiencesOffers = 'randomize_experiences_offers',
  flightsDateGridBar = 'flights_date_grid_bar',
  multiTicketFlights = 'multi_ticket_flights',
  multiTicketMultiCabinFlights = 'multi_ticket_multi_cabin_flights',
  disallowPartialFlightsSeatSelection = 'disallow_partial_flights_seat_selection',
  loyaltyLuxplusLandingPage = 'loyalty_luxplus_landing_page',
  childrenFlyFree = 'children_fly_free',
  flightsPriceAlerts = 'flights_price_alerts',
  /** FLY-5770 kill switch for interleaving campaign banners and price alerts between results. */
  flightsResultsInsertsEnabled = 'flights_results_inserts_enabled',
  croPassportCardExclusiveCheckout = 'cro_passport_card_exclusive_checkout',
  croPassportCardToursCruisesEnabled = 'cro_passport_card_tours_cruises_enabled',
  croCollectOverlappingRoomGuestNames = 'cro_collect_overlapping_room_guest_names',
  croCollectOverlappingRoomGuestNameFlash = 'cro_collect_overlapping_room_guest_name_flash',
  loyaltyEsimBenefit = 'loyalty_esim_benefit',
  searchTypeaheadGeoTypeIndexed = 'search_typeahead_geo_type_indexed',
  /** Sends the flexibleDateSearchV2 rollout to svc-search so flexible date search runs inside search. */
  searchFlexibleDateSearchV2 = 'search_flexible_date_search_v2',
  searchLpcBoost = 'search_lpc_boost',
  searchLppBoost = 'search_lpp_boost',
  searchInclusionBoost = 'search_inclusion_boost',
  flightsFareSense = 'flights_fare_sense',
  opexBookAgain = 'opex_book_again',
  opexSelfServiceNameChange = 'opex_self_service_name_change',
  aiOpsSpoofLaunchPadWhitelist = 'ai-ops-spoof-launch-pad-whitelist',
  aiOpsSpoofUpsellWhitelist = 'ai-ops-spoof-upsell-whitelist',
  leLivePopup = 'le_live_popup',
  experiencesSearchAttractions = 'experiences_search_attractions',
  maldivesCharterFnfPage = 'maldives_charter_fnf_page',
  maldivesCharterReturnFlightFree = 'maldives_charter_return_flight_free',
  charterLaunchOffer100 = 'charter_launch_offer_100',
  loyaltySppPackageUpgrades = 'loyalty_spp_package_upgrades',
  sppMultiIndividualRoom = 'spp_multi_individual_room',
  /** Gates booking accommodationV2-bookable SPP properties through svc-accommodation. */
  sppAccommodationV2Bookings = 'spp_accommodation_v2_bookings',
  myEscapesFlightEticketResend = 'my_escapes_flight_eticket_resend',
  flightsBaggagesAddonCheckout = 'flights_baggages_addon_checkout',
  flightsBaggagesAddonPostPurchase = 'flights_baggages_addon_post_purchase',
  flightsSeatsAddonPostPurchase = 'flights_seats_addon_post_purchase',
  croLocalDestinationOfferCarousel = 'cro_local_destination_offer_carousel',
  bnblV2 = 'voice_ai_bnbl_v2',
}
